import { z } from 'zod';
import { ASPECT_RATIOS } from './schemas';
import { CaptionSchema } from './schemas';
import { GENERATION_MODES, dropLegacyGenerationMode } from '../lib/generation-mode';

export const AITransitionSchema = z.object({
  type: z.enum(['fade', 'slide', 'wipe', 'flip', 'clockWipe']).default('fade'),
  durationSec: z.number().min(0.1).max(2).default(0.5),
  direction: z.enum(['left', 'right', 'up', 'down']).optional(),
});

export type AITransition = z.infer<typeof AITransitionSchema>;

export const AITextStyleSchema = z.object({
  fontFamily: z.string().optional(),
  fontSize: z.number().optional(),
  fontWeight: z.string().optional(),
  color: z.string().optional(),
  textAlign: z.enum(['left', 'center', 'right']).optional(),
  position: z.enum(['top', 'center', 'bottom']).optional(),
  lineHeight: z.number().optional(),
  letterSpacing: z.number().optional(),
  maxWidth: z.number().optional(),
  padding: z.number().optional(),
  backgroundColor: z.string().optional(),
});

export type AITextStyle = z.infer<typeof AITextStyleSchema>;

export const AITextBlockSchema = z.object({
  text: z.string(),
  style: AITextStyleSchema.optional(),
  entrance: AITransitionSchema.optional(),
  animation: z.enum(['none', 'fadeIn', 'slideUp', 'textReveal']).optional(),
});

export type AITextBlock = z.infer<typeof AITextBlockSchema>;

export const AICharacterSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  ageRange: z.string().optional(),
  genderPresentation: z.string().optional(),
  ethnicity: z.string().optional(),
  hair: z.string().optional(),
  wardrobe: z.string().optional(),
  personalityTraits: z.array(z.string()).optional(),
  signatureMannerisms: z.array(z.string()).optional(),
  negativeTraits: z.array(z.string()).optional(),
  referenceImageUrl: z.string().optional(),
  heygenAvatarId: z.string().optional(),
  heygenVoiceId: z.string().optional(),
});

export type AICharacter = z.infer<typeof AICharacterSchema>;

export const AIDialogueLineSchema = z.object({
  id: z.string(),
  speakerCharacterId: z.string(),
  text: z.string(),
});

export type AIDialogueLine = z.infer<typeof AIDialogueLineSchema>;

export const AIVoiceoverClipSchema = z.object({
  id: z.string(),
  kind: z.enum(['narration', 'dialogue']).default('dialogue'),
  url: z.string(),
  startSec: z.number().min(0).default(0),
  durationSec: z.number().min(0).optional(),
  characterId: z.string().optional(),
  voiceId: z.string().optional(),
  voiceName: z.string().optional(),
  text: z.string().optional(),
});

export type AIVoiceoverClip = z.infer<typeof AIVoiceoverClipSchema>;

export const AIPresenterOverlaySchema = z.object({
  enabled: z.boolean().optional(),
  layout: z.enum(['hero-center', 'lower-third', 'rule-of-thirds-left', 'rule-of-thirds-right']).optional(),
  scale: z.number().optional(),
  x: z.number().optional(),
  y: z.number().optional(),
  /** When true, keep the presenter framing safe for a center 1:1 crop of a 9:16 master. */
  safeForSquare: z.boolean().optional(),
  /** Optional cutaway: seconds of background-only before the presenter appears. */
  preRollSec: z.number().min(0).max(5).optional(),
  /** Optional cutaway: seconds of background-only after the presenter disappears. */
  postRollSec: z.number().min(0).max(5).optional(),
});

export type AIPresenterOverlay = z.infer<typeof AIPresenterOverlaySchema>;

export const AISceneSchema = z.object({
  id: z.string(),
  durationSec: z.number().min(0.5).max(60),
  // Optional hint for clip generation pipelines: how many candidate clips to generate for this scene.
  candidateCount: z.number().min(1).max(6).optional(),
  background: z.object({
    type: z.enum(['color', 'image', 'video']),
    value: z.string(),
  }),
  /** Visual-only prompt for background/B-roll generation (used by presenter-composite workflows). */
  brollPrompt: z.string().optional(),
  videoPrompt: z.string().optional(),
  videoModel: z.string().optional(),
  /** Per-scene start image URL for image-to-video providers (Kling, Hailuo, Hunyuan). Used when no character ref matches. */
  startImageUrl: z.string().optional(),
  /**
   * Clip-QC: raw renders must be text-free (default true). Set false for scenes whose keyframe legitimately
   * carries text (signage, truck lettering) so OCR hits are not treated as baked-in hallucinated text.
   */
  expectNoText: z.boolean().optional(),
  characters: z.array(z.string()).optional(),
  continuityNotes: z.string().optional(),
  textBlocks: z.array(AITextBlockSchema).default([]),
  narrationText: z.string().optional(),
  narrationCharacterId: z.string().optional(),
  dialogue: z.array(AIDialogueLineSchema).optional(),
  voiceoverUrl: z.string().optional(),
  voiceoverClips: z.array(AIVoiceoverClipSchema).optional(),
  transition: AITransitionSchema.optional(),
  /** Which generation path to use: 'clip-gen' (default) or 'heygen' (talking-head avatar). */
  videoSource: z.enum(['clip-gen', 'heygen']).optional(),
  /** Presenter overlay configuration for HeyGen+composited scenes. */
  presenterOverlay: AIPresenterOverlaySchema.optional(),
  /** When true, the video background plays WITH audio (e.g. HeyGen avatar speaking) and the separate voiceover track is skipped. */
  backgroundAudioEnabled: z.boolean().optional(),
  /**
   * Legacy flag kept so stored specs keep parsing: no current provider generates native speech
   * (Sora was removed 2026-08). It still enables background audio on the generated clip.
   */
  nativeAudio: z.boolean().optional(),
});

export type AIScene = z.infer<typeof AISceneSchema>;

export const AIVideoSpecSchema = z.object({
  aspectRatio: z.enum(['1:1', '4:5', '9:16', '16:9']).default('1:1'),
  fps: z.number().min(1).max(60).default(30),
  // Stored specs may still carry the removed 'sora-native-lipsync' mode; it parses as unset and is re-inferred.
  generationMode: z.preprocess(dropLegacyGenerationMode, z.enum(GENERATION_MODES).optional()),
  cast: z.array(AICharacterSchema).optional(),
  scenes: z.array(AISceneSchema).min(1),
  // When false, render only the underlying visuals (backgrounds, voiceover, music).
  // Useful for "raw" previews and production workflows where overlays are added later.
  overlayTextEnabled: z.boolean().default(true),
  narrationEnabled: z.boolean().default(true),
  musicUrl: z.string().optional(),
  musicEnabled: z.boolean().default(true),
  musicVolume: z.number().min(0).max(1).optional(),
  /** Set by the compose step once scenes are assembled into a single MP4. */
  finalVideoUrl: z.string().optional(),
  /** Spec-level default for nativeAudio; scene-level overrides take precedence. */
  nativeAudio: z.boolean().optional(),
  stylePreset: z.string().optional(),
  captionStyle: z
    .object({
      fontFamily: z.string().optional(),
      fontSize: z.number().optional(),
      color: z.string().optional(),
      highlightColor: z.string().optional(),
      position: z.enum(['top', 'center', 'bottom']).optional(),
      backgroundColor: z.string().optional(),
    })
    .optional(),
  captions: CaptionSchema.optional(),
});

export type AIVideoSpec = z.infer<typeof AIVideoSpecSchema>;

export const AIPlayerPropsSchema = z.object({
  spec: AIVideoSpecSchema,
});

export type AIPlayerProps = z.infer<typeof AIPlayerPropsSchema>;

export const getDefaultAIVideoSpec = (): AIVideoSpec => ({
  aspectRatio: '1:1',
  fps: 30,
  overlayTextEnabled: true,
  narrationEnabled: true,
  musicEnabled: true,
  stylePreset: 'meta_explainer_dark',
  scenes: [
    {
      id: 'scene-1',
      durationSec: 3,
      background: {
        type: 'color',
        value: '#0f172a',
      },
      textBlocks: [
        {
          text: 'Your AI video',
          style: {
            fontFamily: 'Inter',
            fontSize: 64,
            fontWeight: '700',
            color: '#ffffff',
            textAlign: 'center',
            position: 'center',
          },
          animation: 'textReveal',
          entrance: {
            type: 'fade',
            durationSec: 0.6,
          },
        },
      ],
    },
  ],
});

export function getAIVideoMetrics(spec: AIVideoSpec) {
  const fps = spec.fps || 30;
  const totalSeconds = spec.scenes.reduce((sum, scene) => sum + scene.durationSec, 0);
  const durationInFrames = Math.max(1, Math.round(totalSeconds * fps));
  const ratio = spec.aspectRatio || '1:1';
  const dimensions = ASPECT_RATIOS[ratio] || ASPECT_RATIOS['1:1'];

  return {
    fps,
    durationInFrames,
    width: dimensions.width,
    height: dimensions.height,
  };
}

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
